import { SUMMONER_SPELLS } from '../summonerSpells.js';
import { MAP_CONFIGS } from '../maps.js';
import { listChampionKeys } from '../champions.js';
import {
  isSummonerSpellAllowed,
  sanitizeSummonerPairForMap
} from '../map/summonerSpellMapRules.js';
import { MP_TEST_MAP_ID, orderMapsForLobby } from '../map/mpTestDefaults.js';
import { RUNE_PAGE_PRESETS } from '../runes/runeCatalog.js';
import { resolveRunePage } from '../runes/runePage.js';
import { MASTERY_PAGE_PRESETS } from '../masteries/masteryCatalog.js';
import { resolveMasteryPage } from '../masteries/masteryPage.js';

const LEGACY_RUNE_PAGE_ALIASES = Object.freeze({
  'Standard AD': 'standard_ad'
});

const LEGACY_MASTERY_PAGE_ALIASES = Object.freeze({
  '21/9/0 Offense': 'offense_21_9_0'
});

export class ChampionSelectSession {
  constructor(options = {}) {
    this.roster = options.roster || listChampionKeys();
    this.selectedChampionId = options.selectedChampionId || 'ashe';
    this.availableMaps = options.availableMaps
      || orderMapsForLobby(Object.keys(MAP_CONFIGS));
    this.selectedMapId = options.selectedMapId || MP_TEST_MAP_ID;
    this.spell1 = options.spell1 || 'flash';
    this.spell2 = options.spell2 || 'ignite';
    this.timerSeconds = options.timerSeconds ?? 60;
    this.runePage = this.normalizeRunePageId(
      options.runePage || options.runePageId || 'standard_ad'
    ) || 'standard_ad';
    this.masteryPage = this.normalizeMasteryPageId(
      options.masteryPage || options.masteryPageId || 'offense_21_9_0'
    ) || 'offense_21_9_0';
    this.isLockedIn = false;
    this.phase = 'SELECTING'; // 'SELECTING', 'LOCKED', 'MATCH_STARTING'
    this.sanitizeSpellsForMap();
  }

  normalizeRunePageId(pageId) {
    if (RUNE_PAGE_PRESETS[pageId]) return pageId;
    if (LEGACY_RUNE_PAGE_ALIASES[pageId]) {
      return LEGACY_RUNE_PAGE_ALIASES[pageId];
    }
    return null;
  }

  normalizeMasteryPageId(pageId) {
    if (MASTERY_PAGE_PRESETS[pageId]) return pageId;
    if (LEGACY_MASTERY_PAGE_ALIASES[pageId]) {
      return LEGACY_MASTERY_PAGE_ALIASES[pageId];
    }
    return null;
  }

  setRunePage(pageId) {
    if (this.isLockedIn) return false;
    const normalized = this.normalizeRunePageId(pageId);
    if (!normalized) return false;
    this.runePage = normalized;
    return true;
  }

  setMasteryPage(pageId) {
    if (this.isLockedIn) return false;
    const normalized = this.normalizeMasteryPageId(pageId);
    if (!normalized) return false;
    this.masteryPage = normalized;
    return true;
  }

  selectChampion(championId) {
    if (this.isLockedIn) return false;
    if (!this.roster.includes(championId)) return false;

    this.selectedChampionId = championId;
    return true;
  }

  selectMap(mapId) {
    if (this.isLockedIn) return false;
    if (!this.availableMaps.includes(mapId)) return false;

    this.selectedMapId = mapId;
    this.sanitizeSpellsForMap();
    return true;
  }

  setSummonerSpells(spell1, spell2) {
    if (this.isLockedIn) return false;
    if (!SUMMONER_SPELLS[spell1] || !SUMMONER_SPELLS[spell2]) return false;
    if (spell1 === spell2) return false;
    if (!isSummonerSpellAllowed(spell1, this.selectedMapId)) return false;
    if (!isSummonerSpellAllowed(spell2, this.selectedMapId)) return false;

    this.spell1 = spell1;
    this.spell2 = spell2;
    return true;
  }

  sanitizeSpellsForMap() {
    const [a, b] = sanitizeSummonerPairForMap(
      this.spell1, this.spell2, this.selectedMapId
    );
    this.spell1 = a;
    this.spell2 = b;
  }

  lockIn() {
    if (!this.selectedChampionId || !this.selectedMapId) return false;
    this.isLockedIn = true;
    this.phase = 'LOCKED';
    return true;
  }

  tickTimer() {
    if (this.timerSeconds > 0) {
      this.timerSeconds -= 1;
    }
    if (this.timerSeconds <= 0) {
      this.isLockedIn = true;
      this.phase = 'MATCH_STARTING';
    }
  }

  getMatchConfig() {
    const page = resolveRunePage(this.runePage);
    const mastery = resolveMasteryPage(this.masteryPage);
    return {
      championId: this.selectedChampionId,
      mapId: this.selectedMapId,
      spells: [this.spell1, this.spell2],
      runePage: this.runePage,
      runePageName: page ? page.name : this.runePage,
      masteryPage: this.masteryPage,
      masteryPageName: mastery ? mastery.name : this.masteryPage
    };
  }
}
